import { useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useSearchParams } from "react-router-dom";
import { listKindMeta } from "../api/graph";
import type {
  CatalogItem,
  LibraryItem,
  InitiativeItem,
  ListKind,
  PlatformItem,
} from "../types/navigator";
import { FiltersBar } from "../components/FiltersBar";
import type { FilterOptionGroup } from "../components/FilterPopover";
import { ItemCard } from "../components/ItemCard";
import { ItemModal } from "../components/ItemModal";
import { AddLibraryItemModal } from "../components/AddLibraryItemModal";
import { AddInitiativeItemModal } from "../components/AddInitiativeItemModal";
import { AddInitiativeChatModal } from "../components/AddInitiativeChatModal";
import { AddPlatformItemModal } from "../components/AddPlatformItemModal";
import { PlatformSensitivityLegend } from "../components/PlatformSensitivityLegend";
import { sensitivityInfo, type SensitivityKey } from "../utils/platformSensitivity";
import { EmptyState, LoadingState } from "../components/LoadingState";
import { Pagination } from "../components/Pagination";
import { SORT_OPTIONS, type SortDir, type SortKey } from "../components/SortDropdown";
import type { ViewMode } from "../components/ViewToggle";
import { useListData } from "../hooks/useListData";
import { useIsAdmin } from "../hooks/useIsAdmin";

const HERO_SUBTITLE: Record<ListKind, string> = {
  library:
    "A practical, reusable library of AI solutions and use cases that employees can immediately leverage. Content ranges from simple, high-value prompts (e.g., summarizing Outlook and Teams activity) to fully deployed AI solutions such as automation tools and web scrapers—accelerating adoption by turning proven ideas into repeatable assets.",
  initiative:
    "A transparent portfolio of AI initiatives across the organization, including ideas under consideration, projects in flight, and delivered outcomes. This component also serves as an intake mechanism for new AI ideas, enabling prioritization, visibility, and coordination across teams.",
  platform:
    "A curated view of the AI platforms we have endorsed or are actively evaluating. Each platform entry outlines its purpose, key capabilities, supported use cases, and current status, helping teams understand which tools to use and when—while ensuring alignment with security, compliance, and architectural standards.",
};


const PAGE_SIZE = 24;

// Map UI filter keys -> SharePoint internal field names used in $filter.
// We let the user pick "Status" / "Asset type" etc. and translate to the
// list's actual column name on the way out.
type FilterDef = {
  key: string; // SharePoint internal column name
  label: string;
  // Pull display value from the mapped CatalogItem to suggest options.
  pick: (item: CatalogItem) => string;
};

const FILTERS_BY_KIND: Record<ListKind, FilterDef[]> = {
  library: [
    {
      key: "AssetType",
      label: "Asset type",
      pick: (i) => (i.kind === "library" ? i.assetType : ""),
    },
    {
      key: "BenefitType",
      label: "Benefit",
      pick: (i) => (i.kind === "library" ? i.benefitType : ""),
    },
    {
      key: "AIPlatform",
      label: "Platform",
      pick: (i) => (i.kind === "library" ? i.aiPlatform : ""),
    },
    {
      key: "Department",
      label: "Department",
      pick: (i) => (i.kind === "library" ? i.department : ""),
    },
  ],
  initiative: [
    {
      key: "Status",
      label: "Status",
      pick: (i) => (i.kind === "initiative" ? i.status : ""),
    },
    {
      key: "Priority",
      label: "Priority",
      pick: (i) => (i.kind === "initiative" ? i.priority : ""),
    },
    {
      key: "Region_x002f_Division",
      label: "Region",
      pick: (i) => (i.kind === "initiative" ? i.region : ""),
    },
    {
      key: "Yourname",
      label: "Creator",
      pick: (i) => (i.kind === "initiative" ? i.submittedBy : ""),
    },
  ],
  platform: [
    {
      key: "Status",
      label: "Status",
      pick: (i) => (i.kind === "platform" ? i.status : ""),
    },
    {
      key: "PlatformType",
      label: "Platform type",
      pick: (i) => (i.kind === "platform" ? i.platformType : ""),
    },
    {
      key: "Complexity",
      label: "Complexity",
      pick: (i) => (i.kind === "platform" ? i.complexity : ""),
    },
  ],
};

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

// localStorage-backed state hook scoped per key.
// Re-reads from storage when `key` changes so that per-user namespacing
// takes effect as soon as the MSAL account resolves.
function usePersistedState<T extends string>(
  key: string,
  fallback: T,
  isValid: (v: string) => boolean
): [T, (v: T) => void] {
  const readStorage = (k: string): T => {
    try {
      const raw = window.localStorage.getItem(k);
      if (raw && isValid(raw)) return raw as T;
    } catch {
      // ignore — storage may be blocked
    }
    return fallback;
  };

  const [state, setState] = useState<T>(() => readStorage(key));

  const prevKeyRef = useRef(key);
  useEffect(() => {
    if (key === prevKeyRef.current) return;
    prevKeyRef.current = key;
    setState(readStorage(key));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const update = (v: T) => {
    setState(v);
    try {
      window.localStorage.setItem(key, v);
    } catch {
      // ignore — storage may be blocked
    }
  };
  return [state, update];
}


// Each sort key has a natural default direction.
const DEFAULT_DIR: Record<SortKey, SortDir> = {
  newest: "desc",
  updated: "desc",
  az: "asc",
  status: "asc",
  score: "desc",
};
const isViewMode = (v: string): v is ViewMode =>
  v === "grid" || v === "compact";

function sortItems(items: CatalogItem[], key: SortKey, dir: SortDir): CatalogItem[] {
  const copy = items.slice();
  const d = dir === "asc" ? 1 : -1;
  switch (key) {
    case "newest":
      return copy.sort(
        (a, b) => d * (new Date(a.created ?? 0).getTime() - new Date(b.created ?? 0).getTime())
      );
    case "updated":
      return copy.sort(
        (a, b) => d * (new Date(a.modified ?? 0).getTime() - new Date(b.modified ?? 0).getTime())
      );
    case "az":
      return copy.sort((a, b) => d * a.title.localeCompare(b.title));
    case "status":
      return copy.sort((a, b) => d * (a.status ?? "").localeCompare(b.status ?? ""));
    case "score": {
      const getScore = (item: CatalogItem) => {
        if (item.kind !== "initiative") return -1;
        return (parseFloat(item.businessValueScore) || 0) + (parseFloat(item.technicalFeasibilityScore) || 0);
      };
      return copy.sort((a, b) => d * (getScore(a) - getScore(b)));
    }
  }
}

type Props = {
  kind: ListKind;
};

export function ListPage({ kind }: Props) {
  const meta = useMemo(() => listKindMeta(kind), [kind]);
  const { accounts } = useMsal();
  // Scope all persisted UI prefs to the signed-in user so that two people
  // using the same browser don't bleed their sort/view preferences into each other.
  const userKey = accounts[0]?.username?.toLowerCase() ?? "anon";

  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [selected, setSelected] = useState<CatalogItem | null>(null);
  const [page, setPage] = useState(1);
  const [selectedAssetTypes, setSelectedAssetTypes] = useState<string[]>([]);
  const [sensitivityFilter, setSensitivityFilter] = useState<SensitivityKey | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAddModal, setShowAddModal] = useState(false);
  // Experimental chat-style submission flow — only relevant to "initiative",
  // toggled from a button inside AddInitiativeItemModal. Reset whenever the
  // add-modal closes so reopening always starts back at the classic form.
  const [chatMode, setChatMode] = useState(false);

  // Open the add modal when ?new=1 is in the URL and immediately strip it
  // so a refresh doesn't re-open it.
  useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    setShowAddModal(true);
    setSearchParams(
      (prev) => { const next = new URLSearchParams(prev); next.delete("new"); return next; },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  // Pre-fill the search from ?q= (dashboard links), then strip it the same way.
  useEffect(() => {
    const q = searchParams.get("q");
    if (q === null) return;
    setQuery(q);
    setSearchParams(
      (prev) => { const next = new URLSearchParams(prev); next.delete("q"); return next; },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const isAddSupported = true;

  // Sort is intentionally NOT persisted — always resets to the per-kind default
  // when the user navigates to the page.
  const defaultSortKey: SortKey = kind === "initiative" ? "score" : "newest";
  const [sortKey, setSortKeyRaw] = useState<SortKey>(defaultSortKey);
  const [sortDir, setSortDir] = useState<SortDir>(DEFAULT_DIR[defaultSortKey]);

  const setSortKey = (key: SortKey) => {
    setSortKeyRaw(key);
    setSortDir(DEFAULT_DIR[key]);
  };
  const [viewMode, setViewMode] = usePersistedState<ViewMode>(
    `ai-navigator:list:viewMode:${kind}:${userKey}`,
    "grid",
    isViewMode
  );

  const debouncedQuery = useDebounced(query, 300);

  // Reset state when kind changes
  useEffect(() => {
    setQuery("");
    setFilters({});
    setPage(1);
    setSelectedAssetTypes([]);
    setSensitivityFilter(null);
    const dk: SortKey = kind === "initiative" ? "score" : "newest";
    setSortKeyRaw(dk);
    setSortDir(DEFAULT_DIR[dk]);
  }, [kind]);

  // Reset to page 1 whenever the search or filter set changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, filters, sortKey, selectedAssetTypes, sensitivityFilter]);

  const isAdmin = useIsAdmin();

  const { items: rawItems, loading, error, reset } = useListData<CatalogItem>({
    listId: meta.listId,
    mapper: meta.mapper,
    search: debouncedQuery,
    filters,
  });

  // Non-admins only see Published library items.
  const items = useMemo(() => {
    if (kind !== "library" || isAdmin) return rawItems;
    return rawItems.filter((it) => it.kind === "library" && it.status === "Published");
  }, [rawItems, kind, isAdmin]);

  const defs = FILTERS_BY_KIND[kind];

  const filterGroups: FilterOptionGroup[] = useMemo(() => {
    return defs.map((d) => {
      // Tally each option's count across the loaded items.
      const counts = new Map<string, number>();
      for (const item of items) {
        const v = d.pick(item);
        if (!v) continue;
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
      const opts = Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value));
      return { key: d.key, label: d.label, options: opts };
    });
  }, [defs, items]);

  // Apply client-side sort BEFORE pagination so the page slice is stable.
  const sortedItems = useMemo(
    () => sortItems(items, sortKey, sortDir),
    [items, sortKey, sortDir]
  );

  // Unique asset types derived from loaded library items for the slicer.
  const assetTypeOptions = useMemo(() => {
    if (kind !== "library") return [];
    const types = new Set<string>();
    for (const item of items) {
      if (item.kind === "library" && item.assetType) types.add(item.assetType);
    }
    return Array.from(types).sort();
  }, [items, kind]);

  // Client-side asset type slicer filter (library only).
  const slicerFiltered = useMemo(() => {
    if (selectedAssetTypes.length === 0) return sortedItems;
    return sortedItems.filter(
      (item) => item.kind === "library" && selectedAssetTypes.includes(item.assetType)
    );
  }, [sortedItems, selectedAssetTypes]);

  const toggleAssetType = (type: string) => {
    setSelectedAssetTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // Client-side data-sensitivity quick-filter (platform only) — clicking a
  // legend item filters to that classification; clicking it again clears it.
  // This has to be client-side rather than a server $filter because the
  // classification combines Status + "Approved for use with company data"
  // (see utils/platformSensitivity.ts), not a single field's equality.
  const sensitivityFiltered = useMemo(() => {
    if (kind !== "platform" || !sensitivityFilter) return slicerFiltered;
    return slicerFiltered.filter(
      (item) => item.kind === "platform" && sensitivityInfo(item).key === sensitivityFilter
    );
  }, [slicerFiltered, kind, sensitivityFilter]);

  const toggleSensitivityFilter = (key: SensitivityKey) => {
    setSensitivityFilter((prev) => (prev === key ? null : key));
  };

  // Client-side pagination over sorted+filtered items.
  const total = sensitivityFiltered.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const visible = sensitivityFiltered.slice(startIdx, startIdx + PAGE_SIZE);
  const showingFrom = total === 0 ? 0 : startIdx + 1;
  const showingTo = Math.min(startIdx + PAGE_SIZE, total);

  const hasActiveFilters =
    Object.keys(filters).length > 0 || debouncedQuery.trim().length > 0;
  const clearAllFilters = () => {
    setFilters({});
    setQuery("");
  };

  return (
    <div className="page">
      <div className="page-hero">
        <p className="page-subtitle">{HERO_SUBTITLE[kind]}</p>
      </div>

      {kind === "platform" ? (
        <PlatformSensitivityLegend
          selected={sensitivityFilter}
          onToggle={toggleSensitivityFilter}
        />
      ) : null}

      {error ? (
        <div className="error-banner" role="alert">
          <strong>Couldn't load items.</strong> {error}
        </div>
      ) : null}

      <div className="filters-sticky">
        <FiltersBar
          query={query}
          onQueryChange={setQuery}
          filterGroups={filterGroups}
          activeFilters={filters}
          onFiltersChange={setFilters}
          sortKey={sortKey}
          onSortChange={setSortKey}
          sortDir={sortDir}
          onSortDirChange={setSortDir}
          sortOptions={kind === "initiative" ? SORT_OPTIONS : SORT_OPTIONS.filter(o => o.key !== "score")}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onAddNew={isAddSupported ? () => setShowAddModal(true) : undefined}
          slicerOptions={assetTypeOptions}
          selectedSlicerValues={selectedAssetTypes}
          onSlicerToggle={toggleAssetType}
        />
      </div>

      <div className="results-meta">
        <span>
          {loading
            ? "Loading…"
            : total === 0
            ? "No items match"
            : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
      </div>

      {loading ? (
        <LoadingState view={viewMode} count={viewMode === "compact" ? 6 : 8} />
      ) : visible.length === 0 ? (
        <EmptyState
          hasFilters={hasActiveFilters}
          onClearFilters={clearAllFilters}
        />
      ) : (
        <>
          <div
            className={`card-grid${
              viewMode === "compact" ? " card-grid-compact" : ""
            }`}
          >
            {visible.map((item) => (
              <ItemCard
                key={`${item.kind}-${item.id}`}
                item={item}
                onOpen={setSelected}
                view={viewMode}
              />
            ))}
          </div>
          <Pagination
            page={safePage}
            pageCount={pageCount}
            onPageChange={setPage}
          />
        </>
      )}

      {selected ? (
        <ItemModal
          item={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); reset(); }}
        />
      ) : null}

      {showAddModal && isAddSupported ? (
        <>
          {kind === "library" ? (
            <AddLibraryItemModal
              existing={items.filter(
                (it): it is LibraryItem => it.kind === "library"
              )}
              onClose={() => setShowAddModal(false)}
              onCreated={() => {
                setShowAddModal(false);
                reset();
              }}
            />
          ) : null}
          
          {kind === "initiative" && !chatMode ? (
            <AddInitiativeItemModal
              existing={items.filter(
                (it): it is InitiativeItem => it.kind === "initiative"
              )}
              onClose={() => setShowAddModal(false)}
              onCreated={() => {
                setShowAddModal(false);
                reset();
              }}
              onTryChat={() => setChatMode(true)}
            />
          ) : null}

          {kind === "initiative" && chatMode ? (
            <AddInitiativeChatModal
              existing={items.filter(
                (it): it is InitiativeItem => it.kind === "initiative"
              )}
              onClose={() => {
                setShowAddModal(false);
                setChatMode(false);
              }}
              onCreated={() => {
                setShowAddModal(false);
                setChatMode(false);
                reset();
              }}
              onBackToForm={() => setChatMode(false)}
            />
          ) : null}
          
          {kind === "platform" ? (
            <AddPlatformItemModal
              existing={items.filter(
                (it): it is PlatformItem => it.kind === "platform"
              )}
              onClose={() => setShowAddModal(false)}
              onCreated={() => {
                setShowAddModal(false);
                reset();
              }}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}